import type { IconName } from './components/Icon'

export type ProofPoint = {
  icon: IconName
  label: string
}

export type ServiceStep = {
  icon: IconName
  title: string
  description: string
}

export type TimelineStep = {
  title: string
  description: string
}

export type RoleItem = {
  icon: IconName
  label: string
}

export type ProfileFact = {
  icon: IconName
  label: string
}

export type Faq = {
  id: string
  question: string
  answer: string
}

export const proofPoints: ProofPoint[] = [
  { icon: 'shield', label: 'Qualified CA(SA) professionals' },
  { icon: 'person', label: 'Dedicated remote team members' },
  { icon: 'bank', label: 'South African employment managed' },
  { icon: 'globe', label: 'International framework support' },
]

export const challengePoints = [
  'Limited access to qualified professionals',
  'Seasonal audit and reporting pressure',
  'Long recruitment cycles',
  'Complexity of international hiring',
]

export const serviceSteps: ServiceStep[] = [
  {
    icon: 'search',
    title: 'Search & screen',
    description: 'We find and assess professionals around the role you need.',
  },
  {
    icon: 'bank',
    title: 'Employ locally',
    description: 'We manage South African employment, payroll and support.',
  },
  {
    icon: 'globe',
    title: 'Integrate globally',
    description: 'They join your existing team as dedicated remote colleagues.',
  },
]

export const screeningSteps = [
  'Qualification verification',
  'Employment & reference checks',
  'Technical screening',
  'Communication assessment',
  'Remote working readiness',
  'Role & culture alignment',
  'Framework gap assessment',
]

export const timelineSteps: TimelineStep[] = [
  {
    title: 'Discuss your needs',
    description: 'We understand the role, timing and team context.',
  },
  {
    title: 'Review suitable professionals',
    description: 'We present a carefully selected shortlist.',
  },
  {
    title: 'Interview without obligation',
    description: 'You meet selected candidates with no commitment.',
  },
  {
    title: 'Select and onboard',
    description: 'Ark sets up contracts and payroll; your team leads onboarding.',
  },
  {
    title: 'Work together',
    description: 'Your professional integrates; Ark Solutions keeps supporting.',
  },
]

export const arkResponsibilities: RoleItem[] = [
  { icon: 'search', label: 'Initial screening & qualification checks' },
  { icon: 'document', label: 'South African employment contracts' },
  {
    icon: 'calculator',
    label: 'Payroll administration & statutory processes',
  },
  { icon: 'document', label: 'Leave administration' },
  { icon: 'users', label: 'HR support & employee engagement' },
  { icon: 'chart', label: 'Yearly professional-development budget' },
  { icon: 'headset', label: 'Ongoing placement support' },
]

export const clientResponsibilities: RoleItem[] = [
  { icon: 'inspect', label: 'Interviews & selection' },
  { icon: 'personPlus', label: 'Onboarding' },
  { icon: 'monitor', label: 'Equipment & software' },
  { icon: 'clock', label: 'Daily responsibilities & priorities' },
  { icon: 'settings', label: 'Systems & firm methodologies' },
  { icon: 'chart', label: 'Role training & career development' },
  { icon: 'team', label: 'Team integration' },
  { icon: 'bank', label: 'Any performance bonuses' },
]

export const profileFacts: ProfileFact[] = [
  { icon: 'shield', label: 'Qualified\nCA(SA)' },
  { icon: 'document', label: 'Audit & financial\nreporting experience' },
  {
    icon: 'globe',
    label: 'Experience may include leading international audit firms',
  },
  { icon: 'bank', label: 'IFRS & group\nreporting' },
  { icon: 'monitor', label: 'Full-time remote\navailability' },
]

export const faqColumns: Faq[][] = [
  [
    {
      id: 'initial-discussion',
      question: 'Is there a fee for the initial discussion?',
      answer:
        'No. The initial discussion is free, confidential and carries no obligation to proceed.',
    },
    {
      id: 'candidate-commitment',
      question: 'Are we committed by reviewing candidates?',
      answer:
        'No. You can review and interview suitable professionals before deciding whether to proceed.',
    },
    {
      id: 'employment',
      question: 'Who employs the professional?',
      answer:
        'Ark Solutions employs the professional in South Africa and manages local contracts, payroll administration, statutory processes and employee support.',
    },
    {
      id: 'exclusivity',
      question: 'Does the professional work exclusively for us?',
      answer:
        'The core model is designed around dedicated professionals who integrate into one client team. Any alternative structure would be agreed clearly in advance.',
    },
    {
      id: 'pricing',
      question: 'How is pricing determined?',
      answer:
        'Pricing reflects the role, seniority, technical requirements, working hours and engagement structure. You receive a clear proposal before making any commitment.',
    },
  ],
  [
    {
      id: 'frameworks',
      question: 'Can candidates work under US GAAP or Dutch requirements?',
      answer:
        'CA(SA) professionals enter with strong IFRS grounding and rigorous training. Ark Solutions can coordinate targeted conversion training for US GAAP, Dutch requirements or other client-specific frameworks.',
    },
    {
      id: 'screening',
      question: 'How are candidates screened?',
      answer:
        'The process can include qualification verification, reference checks, technical screening, communication assessment, remote-working readiness and role alignment.',
    },
    {
      id: 'unsuccessful-placement',
      question: 'What happens if a placement is unsuccessful?',
      answer:
        'Ongoing placement support is part of the service. Any rematching or replacement terms will be set out clearly in your engagement proposal.',
    },
    {
      id: 'start-timing',
      question: 'How quickly can a candidate start?',
      answer:
        'Timing depends on availability, notice periods, screening and any framework training. Ark Solutions will provide a realistic onboarding plan once the role is understood.',
    },
    {
      id: 'confidentiality',
      question: 'How is confidential client information protected?',
      answer:
        'Access, systems, confidentiality obligations and data-handling requirements are agreed around your firm’s policies and the professional’s role before work begins.',
    },
  ],
]
